// Libraries
import React from 'react';

// Supermove
import {Loading, ScrollView, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useToast} from '@supermove/hooks';
import {StageModel, UserModel} from '@supermove/models';
import Environment from '@supermove/sdk/src/Environment';
import {openNewTab} from '@supermove/utils';

// App
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import SmallModal from '@shared/design/components/Modal/SmallModal';
import CautionModal from '@shared/design/components/Modal/SmallModal/CautionModal';
import ErrorModal from '@shared/design/components/Modal/SmallModal/ErrorModal';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import ToggleStageIsArchivedForm from '@shared/modules/Project/forms/ToggleStageIsArchivedForm';
import useToggleStageIsArchivedMutation from '@shared/modules/Project/hooks/useToggleStageIsArchivedMutation';

const Row = Styled.View`
  flex-direction: row;
`;

const StyledScrollView = Styled(ScrollView)`
  max-height: 400px;
  padding: 8px;
`;

const ListItems = <T extends {id: string}>({
  listItems,
  getListItemText,
  getListItemUrl,
}: {
  listItems: T[];
  getListItemText: (listItem: T) => string;
  getListItemUrl: (listItem: T) => string;
}) => {
  return (
    <StyledScrollView>
      {listItems.map((listItem) => (
        <Row key={listItem.id}>
          <TertiaryButton
            style={{cursor: 'pointer'}}
            onPress={() => openNewTab(getListItemUrl(listItem))}
            text={`• ${getListItemText(listItem)}`}
          />
        </Row>
      ))}
    </StyledScrollView>
  );
};

const PaddedLoadingIndicator = () => {
  return (
    <React.Fragment>
      <Space height={32} />
      <PageLoadingIndicator />
      <Space height={32} />
    </React.Fragment>
  );
};

const ArchiveModal = ({
  stage,
  isOpen,
  handleClose,
  refetch,
}: {
  stage: StageModel;
  isOpen: boolean;
  handleClose: () => void;
  refetch: () => void;
}) => {
  const handleToggleArchivedSuccessToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Sales stage archived.',
  });

  const {handleSubmit: handleToggleArchived} = useToggleStageIsArchivedMutation({
    form: ToggleStageIsArchivedForm.new({
      stageId: Number(stage.id),
      isArchived: true,
    }),
    onSuccess: () => {
      handleToggleArchivedSuccessToast.handleToast();
      handleClose();
      refetch();
    },
    onError: (errors) => console.log({errors}),
  });

  return (
    <CautionModal
      isOpen={isOpen}
      title={`Archive '${stage.name}'?`}
      message={`New leads won't be assigned to this sales stage.`}
      handlePressOutside={handleClose}
      primaryActionText={'Confirm'}
      handlePrimaryAction={handleToggleArchived}
      secondaryActionText={'Cancel'}
      handleSecondaryAction={handleClose}
    />
  );
};

const CannotArchiveLastStageModal = ({
  stage,
  isOpen,
  handleClose,
}: {
  stage: StageModel;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  return (
    <ErrorModal
      isOpen={isOpen}
      title={`Cannot archive ‘${stage.name}’?`}
      subtitle={`There must be at least one active sales stage for ${stage.projectStatus}. Add another sales stage to archive this one.`}
      closeButtonText={'Close'}
      handleClose={handleClose}
    />
  );
};

const CannotArchiveStageWithProjectsModal = ({
  stage,
  isOpen,
  handleClose,
}: {
  stage: StageModel;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  return (
    <ErrorModal
      isOpen={isOpen}
      title={`Cannot archive ‘${stage.name}’?`}
      subtitle={`There are projects in this sales stage. Remove this sales stage from all projects before archiving`}
      closeButtonText={'Close'}
      handleClose={handleClose}
    />
  );
};

const CannotArchiveStageWithWorkflowsModal = ({
  stage,
  isOpen,
  handleClose,
}: {
  stage: StageModel;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  return (
    <ErrorModal
      isOpen={isOpen}
      title={`Cannot archive ‘${stage.name}’?`}
      subtitle={`This sales stage is used in the following automations:`}
      closeButtonText={'Close'}
      handleClose={handleClose}
    >
      <ListItems
        listItems={stage.workflows}
        getListItemText={(workflow) => workflow.name}
        getListItemUrl={(workflow) =>
          `${Environment.getAppUrl()}/settings/automations/${workflow.uuid}`
        }
      />
    </ErrorModal>
  );
};

const ArchiveStageModal = ({
  stageUuid,
  isOpen,
  handleClose,
  refetch,
}: {
  stageUuid: string;
  isOpen: boolean;
  handleClose: () => void;
  refetch: () => void;
}) => {
  const {data, loading} = useQuery<{viewer: UserModel; stage: StageModel}>(
    ArchiveStageModal.query,
    {
      variables: {
        stageUuid,
      },
      skip: !isOpen,
    },
  );

  if (!data || loading) {
    return (
      <SmallModal handlePressOutside={handleClose} isOpen={isOpen}>
        <Loading loading={loading} as={PaddedLoadingIndicator} />
      </SmallModal>
    );
  } else {
    const isLastStage = data.viewer.viewingOrganization.companySettings.salesStages.length === 1;
    const hasProjects = data.stage.salesProjects.length > 0;
    const hasWorkflows = data.stage.workflows.length > 0;
    if (isLastStage) {
      return (
        <CannotArchiveLastStageModal stage={data.stage} isOpen={isOpen} handleClose={handleClose} />
      );
    } else if (hasProjects) {
      return (
        <CannotArchiveStageWithProjectsModal
          stage={data.stage}
          isOpen={isOpen}
          handleClose={handleClose}
        />
      );
    } else if (hasWorkflows) {
      return (
        <CannotArchiveStageWithWorkflowsModal
          stage={data.stage}
          isOpen={isOpen}
          handleClose={handleClose}
        />
      );
    }

    return (
      <ArchiveModal
        stage={data.stage}
        isOpen={isOpen}
        handleClose={handleClose}
        refetch={refetch}
      />
    );
  }
};

ArchiveStageModal.query = gql`
  query ArchiveStageModal($stageUuid: String!) {
    viewer {
      id
      viewingOrganization {
        id
        companySettings {
          salesStages {
            id
          }
        }
      }
    }
    stage(uuid: $stageUuid) {
      id
      name
      salesProjects {
        id
      }
      workflows {
        id
        uuid
        name
      }
    }
  }
`;

export default ArchiveStageModal;
